import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { approveAdminPractitionerRequest, connectAdminPractitionerCalendar, createAdminCatalogOption, createAdminLocations, createAdminPractitioner, deleteAdminPractitioner, fetchAdminAppointments, fetchAdminLocations, fetchAdminPractitionerRequests, fetchAdminPractitioners, fetchSearchOptionsFromApi, updateAdminAppointmentDecision, updateAdminPractitioner, type AdminPractitionerRequest } from '@/lib/api';

const ADMIN_TOKEN_KEY = 'medicaclic_admin_token';
const ADMIN_USER_KEY = 'medicaclic_admin_user';
const NOTIFICATION_POLL_INTERVAL_MS = 20_000;
const MAX_STORED_NOTIFICATIONS = 50;

function isAdminPractitionerRequest(value: unknown): value is AdminPractitionerRequest {
  if (typeof value !== 'object' || value === null) return false;
  const request = value as Record<string, unknown>;

  return typeof request.id === 'string'
    && ['fiche', 'quote', 'visibility-study', 'contact'].includes(String(request.request_type))
    && typeof request.request_label === 'string'
    && typeof request.name === 'string'
    && typeof request.created_at === 'string';
}

type PractitionerForm = {
  name: string;
  type: string;
  category: string;
  district: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  price: string;
  convention_code: string;
  next_slot: string;
  is_available_now: boolean;
  subscription_tier: string;
  rating: string;
  reviews: string;
  is_featured: boolean;
  availability: Record<string, DayAvailability>;
};

type DayAvailability = {
  enabled: boolean;
  start_time: string;
  end_time: string;
  slot_minutes: number;
};

const weekDays = [
  { key: '1', label: 'Lundi' },
  { key: '2', label: 'Mardi' },
  { key: '3', label: 'Mercredi' },
  { key: '4', label: 'Jeudi' },
  { key: '5', label: 'Vendredi' },
  { key: '6', label: 'Samedi' },
  { key: '0', label: 'Dimanche' },
];

function createEmptyAvailability(): Record<string, DayAvailability> {
  return Object.fromEntries(weekDays.map(({ key }) => [key, {
    enabled: false,
    start_time: '09:00',
    end_time: '17:00',
    slot_minutes: 30,
  }]));
}

function normalizeAvailability(value: unknown): Record<string, DayAvailability> {
  const availability = createEmptyAvailability();
  if (!value || typeof value !== 'object') return availability;

  for (const { key } of weekDays) {
    const day = (value as Record<string, unknown>)[key];
    if (day && typeof day === 'object') {
      availability[key] = { ...availability[key], ...(day as Partial<DayAvailability>) };
    }
  }

  return availability;
}

const emptyForm: PractitionerForm = {
  name: '',
  type: 'Cardiologue',
  category: 'Médecins',
  district: 'Agdal',
  city: 'Rabat',
  address: '',
  phone: '',
  email: '',
  price: '350',
  convention_code: 'CNOPS',
  next_slot: 'Aujourd’hui · 15:00',
  is_available_now: true,
  subscription_tier: 'premium',
  rating: '4.8',
  reviews: '42',
  is_featured: false,
  availability: createEmptyAvailability(),
};

function IconDashboard({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path d="M4 13.5V5.5A1.5 1.5 0 0 1 5.5 4H10v7.5H4Zm10 0V4h4.5A1.5 1.5 0 0 1 20 5.5v8Zm-10 6V13.5h6V20H5.5A1.5 1.5 0 0 1 4 18.5Zm10 0v-6h6v4.5a1.5 1.5 0 0 1-1.5 1.5H14Z" />
    </svg>
  );
}

function IconUsers({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path d="M16 19v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1" />
      <circle cx="10" cy="7" r="3" />
      <path d="M20 19v-1a4 4 0 0 0-3-3.87" />
      <path d="M16 4.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconCalendar({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  );
}

function IconChart({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path d="M5 19V9M12 19V5M19 19v-7" />
      <path d="M3 19h18" />
    </svg>
  );
}

function IconSettings({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.07V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-.4-1.07 1.7 1.7 0 0 0-1-.6 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.07-.4H2.84a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.07-.4 1.7 1.7 0 0 0 .6-1A1.7 1.7 0 0 0 4.6 7.12l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.07V2.84a2 2 0 0 1 4 0v.09c0 .4.14.79.4 1.07.27.27.66.4 1.07.4a1.7 1.7 0 0 0 1-.6l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.27.27.4.66.4 1.07v.09a2 2 0 0 1 0 4h-.09c-.4 0-.79.14-1.07.4-.27.27-.4.66-.4 1.07Z" />
    </svg>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [practitioners, setPractitioners] = useState<Record<string, unknown>[]>([]);
  const [specialties, setSpecialties] = useState<string[]>(['Cardiologue']);
  const [categories, setCategories] = useState<string[]>(['Médecins']);
  const [locations, setLocations] = useState<Record<string, string[]>>({ Rabat: ['Agdal'] });
  const [appointments, setAppointments] = useState<Record<string, unknown>[]>([]);
  const [practitionerRequests, setPractitionerRequests] = useState<AdminPractitionerRequest[]>([]);
  const [notifications, setNotifications] = useState<AdminPractitionerRequest[]>([]);
  const [readNotificationIds, setReadNotificationIds] = useState<string[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationStorageKeys = useRef<{ notifications: string; knownRequests: string } | null>(null);
  const notificationsRef = useRef<AdminPractitionerRequest[]>([]);
  const [practitionerRequestFilter, setPractitionerRequestFilter] = useState<'all' | AdminPractitionerRequest['request_type']>('all');
  const [selectedAppointment, setSelectedAppointment] = useState<Record<string, unknown> | null>(null);
  const [practitionerSearch, setPractitionerSearch] = useState('');
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
  const [practitionerPage, setPractitionerPage] = useState(1);
  const [appointmentPage, setAppointmentPage] = useState(1);
  const [form, setForm] = useState<PractitionerForm>(emptyForm);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('Vue d’ensemble');
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [newCity, setNewCity] = useState('');
  const [newDistricts, setNewDistricts] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [savingCatalogOption, setSavingCatalogOption] = useState<'specialties' | 'categories' | null>(null);
  const [catalogOptionError, setCatalogOptionError] = useState('');
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [calendarNotice, setCalendarNotice] = useState('');
  const [calendarPractitionerId] = useState(() => new URLSearchParams(window.location.search).get('calendar_practitioner'));
  const [approvingProspectId, setApprovingProspectId] = useState<number | null>(null);
  const [approvalRequest, setApprovalRequest] = useState<AdminPractitionerRequest | null>(null);
  const [approvalPlan, setApprovalPlan] = useState<'standard' | 'ars' | 'premium' | 'convention'>('standard');
  const [approvalConventionCode, setApprovalConventionCode] = useState('');
  const [approvalError, setApprovalError] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem(ADMIN_USER_KEY);
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);

    if (!token || !storedUser) {
      navigate('/admin/login');
      return;
    }

    setUser(JSON.parse(storedUser));
    void loadPractitioners();
    void loadAppointments();
    void fetchAdminLocations()
      .then(setLocations)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Erreur lors du chargement des villes'));
    void fetchSearchOptionsFromApi()
      .then((options) => {
        setSpecialties(options.specialties);
        setCategories(options.categories);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Erreur lors du chargement des spécialités'));
  }, [navigate]);

  useEffect(() => {
    if (!user?.email) return;

    const accountKey = encodeURIComponent(user.email.toLowerCase());
    const storageKeys = {
      notifications: `medicaclic_admin_notifications:${accountKey}`,
      knownRequests: `medicaclic_admin_known_requests:${accountKey}`,
    };
    notificationStorageKeys.current = storageKeys;

    let knownRequestIds = new Set<string>();
    let hasInitialSnapshot = false;
    let storedNotifications: AdminPractitionerRequest[] = [];
    let storedReadIds: string[] = [];

    try {
      const knownValue = localStorage.getItem(storageKeys.knownRequests);
      if (knownValue !== null) {
        const parsedKnown: unknown = JSON.parse(knownValue);
        if (!Array.isArray(parsedKnown) || !parsedKnown.every((id) => typeof id === 'string')) {
          throw new Error('La liste des demandes déjà consultées est invalide.');
        }
        knownRequestIds = new Set(parsedKnown);
        hasInitialSnapshot = true;
      }

      const notificationValue = localStorage.getItem(storageKeys.notifications);
      if (notificationValue !== null) {
        const parsedNotifications: unknown = JSON.parse(notificationValue);
        if (!Array.isArray(parsedNotifications) || !parsedNotifications.every(isAdminPractitionerRequest)) {
          throw new Error('La liste des notifications enregistrées est invalide.');
        }
        storedNotifications = parsedNotifications;
      }

      const readValue = localStorage.getItem(`${storageKeys.notifications}:read`);
      if (readValue !== null) {
        const parsedReadIds: unknown = JSON.parse(readValue);
        if (!Array.isArray(parsedReadIds) || !parsedReadIds.every((id) => typeof id === 'string')) {
          throw new Error('La liste des notifications lues est invalide.');
        }
        storedReadIds = parsedReadIds;
      }
    } catch (storageError) {
      setError(storageError instanceof Error ? storageError.message : 'Impossible de lire les notifications enregistrées.');
    }

    notificationsRef.current = storedNotifications;
    setNotifications(storedNotifications);
    setReadNotificationIds(storedReadIds);

    let stopped = false;
    let polling = false;
    const refreshRequests = async () => {
      if (polling || stopped) return;
      polling = true;

      try {
        const requests = await fetchAdminPractitionerRequests();
        if (stopped) return;
        setPractitionerRequests(requests);

        const newRequests = hasInitialSnapshot
          ? requests.filter((request) => !knownRequestIds.has(request.id))
          : [];
        requests.forEach((request) => knownRequestIds.add(request.id));
        hasInitialSnapshot = true;
        localStorage.setItem(storageKeys.knownRequests, JSON.stringify([...knownRequestIds]));

        if (newRequests.length > 0) {
          const merged = [...newRequests, ...notificationsRef.current.filter(
            (notification) => !newRequests.some((request) => request.id === notification.id),
          )].slice(0, MAX_STORED_NOTIFICATIONS);
          notificationsRef.current = merged;
          localStorage.setItem(storageKeys.notifications, JSON.stringify(merged));
          setNotifications(merged);
        }
      } catch (pollError) {
        if (!stopped) {
          setError(pollError instanceof Error ? pollError.message : 'Impossible de vérifier les nouvelles demandes.');
        }
      } finally {
        polling = false;
      }
    };

    void refreshRequests();
    const intervalId = window.setInterval(() => void refreshRequests(), NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      notificationStorageKeys.current = null;
    };
  }, [user?.email]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const result = query.get('calendar');
    const messages: Record<string, string> = {
      connected: 'Google Calendar connecté avec succès.',
      cancelled: 'La connexion Google Calendar a été annulée.',
      error: 'Impossible de connecter Google Calendar. Réessayez.',
      invalid: 'La demande de connexion a expiré. Réessayez.',
    };

    if (result && messages[result]) {
      setCalendarNotice(messages[result]);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPractitioners = async () => {
    try {
      const data = await fetchAdminPractitioners();
      setPractitioners(data);
      const calendarPractitioner = calendarPractitionerId
        ? data.find((practitioner) => String(practitioner.id) === calendarPractitionerId)
        : undefined;
      if (calendarPractitioner) {
        handleEdit(calendarPractitioner);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    }
  };

  const loadAppointments = async () => {
    try {
      const data = await fetchAdminAppointments();
      setAppointments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des rendez-vous');
    }
  };

  const loadPractitionerRequests = async () => {
    try {
      setPractitionerRequests(await fetchAdminPractitionerRequests());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des demandes praticiens');
    }
  };

  const beginPractitionerApproval = (request: AdminPractitionerRequest) => {
    setApprovalRequest(request);
    setApprovalPlan('standard');
    setApprovalConventionCode('');
    setApprovalError('');
  };

  const handleApprovePractitionerRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!approvalRequest?.prospect_id) return;

    setApprovingProspectId(approvalRequest.prospect_id);
    setApprovalError('');
    try {
      await approveAdminPractitionerRequest(approvalRequest.prospect_id, {
        subscription_tier: approvalPlan,
        ...(approvalPlan === 'convention' ? { convention_code: approvalConventionCode.trim() } : {}),
      });
      setApprovalRequest(null);
      await Promise.all([loadPractitioners(), loadPractitionerRequests()]);
      try {
        const options = await fetchSearchOptionsFromApi();
        setSpecialties(options.specialties);
        setCategories(options.categories);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des spécialités');
      }
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : 'Impossible de confirmer cette demande.');
    } finally {
      setApprovingProspectId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    navigate('/admin/login');
  };

  const handleChange = (field: keyof PractitionerForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCatalogOption = async (kind: 'specialties' | 'categories') => {
    const name = (kind === 'specialties' ? newSpecialty : newCategory).trim();
    if (!name) return;

    setSavingCatalogOption(kind);
    setCatalogOptionError('');
    try {
      const option = await createAdminCatalogOption(kind, name);
      if (kind === 'specialties') {
        setSpecialties((current) => Array.from(new Set([...current, option.name])).sort((a, b) => a.localeCompare(b, 'fr')));
        handleChange('type', option.name);
        setNewSpecialty('');
      } else {
        setCategories((current) => Array.from(new Set([...current, option.name])).sort((a, b) => a.localeCompare(b, 'fr')));
        handleChange('category', option.name);
        setNewCategory('');
      }
    } catch (err) {
      setCatalogOptionError(err instanceof Error ? err.message : 'Impossible d’ajouter cette option.');
    } finally {
      setSavingCatalogOption(null);
    }
  };

  const handleAvailabilityChange = (day: string, field: keyof DayAvailability, value: boolean | number | string) => {
    setForm((current) => ({
      ...current,
      availability: {
        ...current.availability,
        [day]: { ...current.availability[day], [field]: value },
      },
    }));
  };

  const handleConnectCalendar = async () => {
    if (editingId === null) return;
    setConnectingCalendar(true);
    setError('');

    try {
      const { authorization_url } = await connectAdminPractitionerCalendar(editingId);
      window.location.assign(authorization_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de démarrer la connexion Google Calendar.');
      setConnectingCalendar(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { price: practitionerPrice, reviews: practitionerReviews, next_slot: practitionerNextSlot, ...formFields } = form;
      const payload = {
        ...formFields,
        address: form.address.trim().split(',').at(-1)?.trim() === form.city
          ? form.address.trim()
          : [form.address.trim(), form.city].filter(Boolean).join(', '),
        rating: Number(form.rating),
        is_available_now: Boolean(form.is_available_now),
        is_featured: Boolean(form.is_featured),
        ...(editingId !== null ? { price: Number(practitionerPrice) } : {}),
        ...(editingId !== null ? { reviews: Number(practitionerReviews) } : {}),
        ...(editingId !== null ? { next_slot: practitionerNextSlot } : {}),
      };

      if (editingId !== null) {
        await updateAdminPractitioner(editingId, payload);
      } else {
        await createAdminPractitioner(payload);
      }

      setForm(emptyForm);
      setEditingId(null);
      setError('');
      await loadPractitioners();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer le praticien');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (practitioner: Record<string, unknown>) => {
    const city = String(practitioner.city ?? String(practitioner.address ?? '').split(',').at(-1)?.trim() ?? 'Rabat');
    const nextForm: PractitionerForm = {
      name: String(practitioner.name ?? ''),
      type: String(practitioner.type ?? 'Cardiologue'),
      category: String(practitioner.category ?? 'Médecins'),
      district: String(practitioner.district ?? 'Agdal'),
      city,
      address: String(practitioner.address ?? ''),
      phone: String(practitioner.phone ?? ''),
      email: String(practitioner.email ?? ''),
      price: String(practitioner.price ?? '350'),
      convention_code: String(practitioner.convention_code ?? 'CNOPS'),
      next_slot: String(practitioner.next_slot ?? 'Aujourd’hui · 15:00'),
      availability: normalizeAvailability(practitioner.availability),
      is_available_now: Boolean(practitioner.is_available_now),
      subscription_tier: String(practitioner.subscription_tier ?? 'premium'),
      rating: String(practitioner.rating ?? '4.8'),
      reviews: String(practitioner.reviews ?? '42'),
      is_featured: Boolean(practitioner.is_featured),
    };

    setForm(nextForm);
    setCalendarConnected(Boolean(practitioner.google_calendar_connected));
    setLocations((current) => ({
      ...current,
      [city]: Array.from(new Set([...(current[city] ?? []), nextForm.district].filter(Boolean))),
    }));
    setEditingId(practitioner.id != null ? String(practitioner.id) : null);
  };

  const handleCancelEdit = () => {
    setForm(emptyForm);
    setEditingId(null);
    setCalendarConnected(false);
  };

  const handleAddLocation = async () => {
    setError('');

    const districts = Array.from(new Set(newDistricts.split(/[\n,;]/).map((district) => district.trim()).filter(Boolean)));

    try {
      const savedLocations = await createAdminLocations({ city: newCity.trim(), districts });
      setLocations(savedLocations);
      setForm((current) => ({ ...current, city: newCity.trim(), district: districts[0] ?? '' }));
      setNewCity('');
      setNewDistricts('');
      setShowLocationForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’ajouter cette ville.');
    }
  };

  const handleDelete = async (id: number | string) => {
    try {
      await deleteAdminPractitioner(id);
      await loadPractitioners();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de supprimer le praticien');
    }
  };

  const handleAppointmentDecision = async (id: number | string, decision: 'confirmed' | 'cancelled') => {
    try {
      await updateAdminAppointmentDecision(id, decision);
      await loadAppointments();
      setSelectedAppointment((current) => {
        if (!current || String(current.id) !== String(id)) {
          return current;
        }

        return {
          ...current,
          status: decision,
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de mettre à jour le rendez-vous');
    }
  };

  const filteredPractitioners = practitioners.filter((practitioner) => {
    const query = practitionerSearch.trim().toLowerCase();
    if (!query) {
      return true;
    }

    const name = String(practitioner.name ?? '').toLowerCase();
    const type = String(practitioner.type ?? '').toLowerCase();
    const district = String(practitioner.district ?? '').toLowerCase();

    return name.includes(query) || type.includes(query) || district.includes(query);
  });

  const filteredAppointments = appointments.filter((appointment) => {
    if (appointmentStatusFilter === 'all') {
      return true;
    }

    return String(appointment.status ?? 'pending') === appointmentStatusFilter;
  });

  const filteredPractitionerRequests = practitionerRequests.filter((request) => (
    practitionerRequestFilter === 'all' || request.request_type === practitionerRequestFilter
  ));

  const practitionerPageSize = 6;
  const appointmentPageSize = 5;
  const practitionerTotalPages = Math.max(1, Math.ceil(filteredPractitioners.length / practitionerPageSize));
  const appointmentTotalPages = Math.max(1, Math.ceil(filteredAppointments.length / appointmentPageSize));

  const paginatedPractitioners = filteredPractitioners.slice(
    (practitionerPage - 1) * practitionerPageSize,
    practitionerPage * practitionerPageSize,
  );
  const paginatedAppointments = filteredAppointments.slice(
    (appointmentPage - 1) * appointmentPageSize,
    appointmentPage * appointmentPageSize,
  );

  useEffect(() => {
    setPractitionerPage((page) => Math.min(page, practitionerTotalPages));
    setAppointmentPage((page) => Math.min(page, appointmentTotalPages));
  }, [practitionerTotalPages, appointmentTotalPages]);

  const handleExportAppointments = () => {
    const rows = filteredAppointments.map((appointment) => ({
      patient_name: String(appointment.patient_name ?? ''),
      patient_phone: String(appointment.patient_phone ?? ''),
      practitioner_name: String(appointment.practitioner_name ?? ''),
      appointment_date: String(appointment.appointment_date ?? ''),
      appointment_time: String(appointment.appointment_time ?? ''),
      status: String(appointment.status ?? 'pending'),
      notes: String(appointment.notes ?? ''),
    }));

    const headers = ['patient_name', 'patient_phone', 'practitioner_name', 'appointment_date', 'appointment_time', 'status', 'notes'];
    const csvRows = [headers.join(',')].concat(rows.map((row) => headers.map((header) => {
      const value = String((row as Record<string, string>)[header] ?? '').replace(/"/g, '""');
      return `"${value}"`;
    }).join(',')));

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `medicaclic-rendez-vous-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportPractitioners = () => {
    const rows = filteredPractitioners.map((practitioner) => ({
      name: String(practitioner.name ?? ''),
      type: String(practitioner.type ?? ''),
      category: String(practitioner.category ?? ''),
      district: String(practitioner.district ?? ''),
      city: String(practitioner.city ?? ''),
      address: String(practitioner.address ?? ''),
      phone: String(practitioner.phone ?? ''),
      email: String(practitioner.email ?? ''),
      price: String(practitioner.price ?? ''),
      convention_code: String(practitioner.convention_code ?? ''),
      next_slot: String(practitioner.next_slot ?? ''),
      is_available_now: practitioner.is_available_now ? 'Oui' : 'Non',
      subscription_tier: String(practitioner.subscription_tier ?? ''),
      rating: String(practitioner.rating ?? ''),
      reviews: String(practitioner.reviews ?? ''),
      is_featured: practitioner.is_featured ? 'Oui' : 'Non',
    }));

    const headers = ['name', 'type', 'category', 'city', 'district', 'address', 'phone', 'email', 'price', 'convention_code', 'next_slot', 'is_available_now', 'subscription_tier', 'rating', 'reviews', 'is_featured'];
    const csvRows = [headers.join(',')].concat(rows.map((row) => headers.map((header) => {
      const value = String((row as Record<string, string>)[header] ?? '').replace(/"/g, '""');
      return `"${value}"`;
    }).join(',')));

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `medicaclic-praticiens-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const performanceBars = [52, 76, 58, 82, 64, 91, 76];
  const appointmentBars = [38, 44, 58, 52, 72, 66, 80];

  const totalAppointments = appointments.length;
  const pendingAppointments = appointments.filter((appointment) => String(appointment.status ?? 'pending') === 'pending').length;
  const confirmedAppointments = appointments.filter((appointment) => String(appointment.status ?? 'pending') === 'confirmed').length;
  const conversionRate = totalAppointments > 0 ? Math.round((confirmedAppointments / totalAppointments) * 100) : 0;
  const averagePrice = practitioners.length > 0
    ? Math.round(practitioners.reduce((sum, practitioner) => sum + (Number(practitioner.price) || 0), 0) / practitioners.length)
    : 0;
  const featuredCount = practitioners.filter((practitioner) => practitioner.is_featured === true).length;
  const availableNow = practitioners.filter((practitioner) => practitioner.is_available_now === true).length;

  const summaryStats = [
    {
      title: 'Praticiens actifs',
      value: practitioners.length,
      delta: '+12%',
      tone: 'emerald',
      bars: performanceBars,
    },
    {
      title: 'Disponibles maintenant',
      value: availableNow,
      delta: 'Live',
      tone: 'sky',
      bars: appointmentBars,
    },
    {
      title: 'Mise en avant',
      value: featuredCount,
      delta: 'Featured',
      tone: 'amber',
      bars: performanceBars.slice(0, 7),
    },
  ];

  const navItems = [
    { label: 'Vue d’ensemble', icon: IconDashboard },
    { label: 'Praticiens', icon: IconUsers },
    { label: 'Rendez-vous', icon: IconCalendar },
    { label: 'Demandes praticiens', icon: IconUsers },
    { label: 'Rapports', icon: IconChart },
    { label: 'Paramètres', icon: IconSettings },
  ];

  const handleSectionClick = (label: string) => {
    setActiveSection(label);

    if (label === 'Paramètres') {
      navigate('/admin/profile');
      return;
    }

    const sectionMap: Record<string, string> = {
      'Vue d’ensemble': 'overview-section',
      Praticiens: 'practitioners-section',
      'Rendez-vous': 'appointments-section',
      'Demandes praticiens': 'practitioner-requests-section',
      Rapports: 'overview-section',
    };

    const sectionId = sectionMap[label];
    if (sectionId) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const unreadNotificationCount = notifications.filter(
    (notification) => !readNotificationIds.includes(notification.id),
  ).length;

  const toggleNotifications = () => {
    const opening = !notificationsOpen;
    setNotificationsOpen(opening);
    if (!opening) return;

    const nextReadIds = Array.from(new Set([
      ...readNotificationIds,
      ...notifications.map((notification) => notification.id),
    ]));
    setReadNotificationIds(nextReadIds);

    const storageKeys = notificationStorageKeys.current;
    if (storageKeys) {
      try {
        localStorage.setItem(`${storageKeys.notifications}:read`, JSON.stringify(nextReadIds));
      } catch (storageError) {
        setError(storageError instanceof Error ? storageError.message : 'Impossible d’enregistrer les notifications lues.');
      }
    }
  };

  const openNotification = () => {
    setNotificationsOpen(false);
    handleSectionClick('Demandes praticiens');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100 p-3 text-slate-800 md:p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-[0_20px_60px_rgba(15,23,42,0.14)] md:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-200">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                Admin
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Dashboard Medicaclic</h1>
                <p className="mt-1 text-sm text-slate-300">Bienvenue, {user.name}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={toggleNotifications}
                  aria-label={`Notifications${unreadNotificationCount > 0 ? ` : ${unreadNotificationCount} non lue(s)` : ''}`}
                  aria-expanded={notificationsOpen}
                  className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                    <path d="M10 21h4" />
                  </svg>
                  {unreadNotificationCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-xl">
                    <div className="border-b border-slate-100 px-4 py-3">
                      <h2 className="font-semibold">Notifications</h2>
                      <p className="text-xs text-slate-500">Nouvelles demandes reçues</p>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-slate-500">Aucune nouvelle notification.</p>
                      ) : notifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={openNotification}
                          className="block w-full border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50"
                        >
                          <span className="flex items-start gap-2">
                            {!readNotificationIds.includes(notification.id) && (
                              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                            )}
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold">{notification.request_label}</span>
                              <span className="mt-0.5 block truncate text-sm text-slate-600">{notification.name}</span>
                              <span className="mt-1 block text-xs text-slate-400">
                                Reçue le {new Date(notification.created_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                              </span>
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        onClick={openNotification}
                        className="w-full px-4 py-3 text-sm font-medium text-primary-700 transition hover:bg-primary-50"
                      >
                        Voir les demandes praticiens
                      </button>
                    )}
                  </div>
                )}
              </div>
              <Link to="/" className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10">
                Retour au site
              </Link>
              <button onClick={handleLogout} className="rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-400">
                Déconnexion
              </button>
            </div>
          </div>
        </header>

        <div className="grid gap-6 xl:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="rounded-[28px] bg-slate-950 p-4 text-white shadow-[0_20px_50px_rgba(15,23,42,0.12)] xl:sticky xl:top-6 xl:h-fit">
            <div className="mb-6 flex items-center gap-3 rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-500/20 text-lg font-bold text-primary-200">M</div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Platform</p>
                <p className="text-base font-semibold">Medicaclic</p>
              </div>
            </div>

            <nav className="space-y-2">
              {navItems.map(({ label, icon: Icon }) => {
                const isActive = activeSection === label;

                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleSectionClick(label)}
                    className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm transition ${isActive ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Icon className="h-4 w-4" />
                      {label}
                    </span>
                    {isActive && <span className="text-xs">•</span>}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="space-y-6">
            <section id="overview-section" className="grid gap-4 md:grid-cols-3">
              {summaryStats.map(({ title, value, delta, tone, bars }) => (
                <div key={title} className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">{title}</p>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' : tone === 'sky' ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
                      {delta}
                    </span>
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-3">
                    <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
                    <div className="flex h-10 items-end gap-1">
                      {bars.map((barValue, index) => (
                        <span
                          key={`${title}-${index}`}
                          className={`w-2 rounded-full ${tone === 'emerald' ? 'bg-emerald-200' : tone === 'sky' ? 'bg-sky-200' : 'bg-amber-200'}`}
                          style={{ height: `${barValue}%` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Performance</p>
                    <h2 className="mt-1 text-xl font-semibold text-slate-900">Activité du mois</h2>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">+{conversionRate}% conversion</span>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">Rdv en attente</p>
                    <p className="mt-3 text-2xl font-bold text-slate-900">{pendingAppointments}</p>
                    <p className="mt-2 text-xs text-slate-500">À traiter</p>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-4">
                    <p className="text-sm text-emerald-700">Confirmés</p>
                    <p className="mt-3 text-2xl font-bold text-emerald-900">{confirmedAppointments}</p>
                    <p className="mt-2 text-xs text-emerald-700">Taux OK</p>
                  </div>
                  <div className="rounded-2xl bg-primary-50 p-4">
                    <p className="text-sm text-primary-700">Prix moyen</p>
                    <p className="mt-3 text-2xl font-bold text-primary-900">{averagePrice} MAD</p>
                    <p className="mt-2 text-xs text-primary-700">Par praticien</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Overview</p>
                    <h2 className="mt-1 text-xl font-semibold text-slate-900">Synthèse</h2>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-slate-50 p-3">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>Disponibilité</span>
                      <span className="font-semibold text-slate-900">{availableNow}/{practitioners.length}</span>
                    </div>
                    <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${practitioners.length ? (availableNow / practitioners.length) * 100 : 0}%` }} />
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>Conversion</span>
                      <span className="font-semibold text-slate-900">{conversionRate}%</span>
                    </div>
                    <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${conversionRate}%` }} />
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>Featured</span>
                      <span className="font-semibold text-slate-900">{featuredCount}</span>
                    </div>
                    <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-amber-500" style={{ width: `${practitioners.length ? (featuredCount / practitioners.length) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div id="practitioners-section" className="grid gap-6 2xl:grid-cols-[400px_minmax(0,1fr)]">
              <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Gestion</p>
                    <h2 className="mt-1 text-2xl font-bold text-slate-900">{editingId !== null ? 'Modifier le praticien' : 'Ajouter un praticien'}</h2>
                  </div>
                  {editingId !== null && (
                    <button type="button" onClick={handleCancelEdit} className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      Annuler
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Nom</label>
                    <input value={form.name} onChange={(e) => handleChange('name', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" required />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Spécialité</label>
                      <select value={form.type} onChange={(e) => handleChange('type', e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 focus:border-primary-500 focus:outline-none" required>
                        {Array.from(new Set([...specialties, form.type])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr')).map((specialty) => (
                          <option key={specialty} value={specialty}>{specialty}</option>
                        ))}
                      </select>
                      <div className="mt-2 flex gap-2">
                        <input
                          value={newSpecialty}
                          onChange={(event) => setNewSpecialty(event.target.value)}
                          placeholder="Nouvelle spécialité"
                          maxLength={255}
                          aria-label="Nouvelle spécialité"
                          className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => void handleAddCatalogOption('specialties')}
                          disabled={!newSpecialty.trim() || savingCatalogOption !== null}
                          className="rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-800 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingCatalogOption === 'specialties' ? 'Ajout…' : 'Ajouter'}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Catégorie</label>
                      <select value={form.category} onChange={(e) => handleChange('category', e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 focus:border-primary-500 focus:outline-none" required>
                        {Array.from(new Set([...categories, form.category])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr')).map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <div className="mt-2 flex gap-2">
                        <input
                          value={newCategory}
                          onChange={(event) => setNewCategory(event.target.value)}
                          placeholder="Nouvelle catégorie"
                          maxLength={255}
                          aria-label="Nouvelle catégorie"
                          className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => void handleAddCatalogOption('categories')}
                          disabled={!newCategory.trim() || savingCatalogOption !== null}
                          className="rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-800 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingCatalogOption === 'categories' ? 'Ajout…' : 'Ajouter'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {catalogOptionError && (
                    <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {catalogOptionError}
                    </p>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Ville</label>
                      <select
                        value={form.city}
                        onChange={(event) => {
                          const city = event.target.value;
                          handleChange('city', city);
                          handleChange('district', locations[city]?.[0] ?? '');
                        }}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 focus:border-primary-500 focus:outline-none"
                        required
                      >
                        {Object.keys(locations).sort((a, b) => a.localeCompare(b, 'fr')).map((city) => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Quartier</label>
                      <select
                        value={form.district}
                        onChange={(event) => handleChange('district', event.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 focus:border-primary-500 focus:outline-none"
                        required
                      >
                        {form.district && !locations[form.city]?.includes(form.district) && <option value={form.district}>{form.district}</option>}
                        {(locations[form.city] ?? []).map((district) => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowLocationForm((visible) => !visible)}
                      aria-expanded={showLocationForm}
                      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-800 transition hover:border-primary-300 hover:bg-primary-100 sm:w-auto"
                    >
                      <span aria-hidden="true" className="text-lg leading-none">{showLocationForm ? '−' : '+'}</span>
                      {showLocationForm ? 'Fermer' : 'Ajouter une ville'}
                    </button>

                    {showLocationForm && (
                      <div className="mt-3 rounded-2xl border border-primary-100 bg-primary-50/60 p-4">
                        <p className="text-sm font-semibold text-slate-900">Nouvelle ville</p>
                        <div className="mt-3 space-y-3">
                          <div>
                            <label htmlFor="new-city" className="mb-1.5 block text-xs font-medium text-slate-700">Ville</label>
                            <input
                              id="new-city"
                              value={newCity}
                              onChange={(event) => setNewCity(event.target.value)}
                              placeholder="Ex. Casablanca"
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label htmlFor="new-districts" className="mb-1.5 block text-xs font-medium text-slate-700">Quartiers</label>
                            <textarea
                              id="new-districts"
                              value={newDistricts}
                              onChange={(event) => setNewDistricts(event.target.value)}
                              placeholder={'Un quartier par ligne\nMaarif\nAnfa'}
                              rows={3}
                              className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleAddLocation()}
                            className="w-full rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
                          >
                            Ajouter la ville et les quartiers
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {form.subscription_tier !== 'convention' && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Code convention</label>
                      <input value={form.convention_code} onChange={(e) => handleChange('convention_code', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                    </div>
                  )}

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Adresse</label>
                    <input value={form.address} onChange={(e) => handleChange('address', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className={editingId === null ? 'sm:col-span-2' : ''}>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Téléphone</label>
                      <input value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                    </div>
                    {editingId !== null && (
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">Prix</label>
                        <input type="number" value={form.price} onChange={(e) => handleChange('price', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="practitioner-email" className="mb-1.5 block text-sm font-medium text-slate-700">Gmail / E-mail</label>
                    <input
                      id="practitioner-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(event) => handleChange('email', event.target.value)}
                      placeholder="exemple@gmail.com"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Agenda hebdomadaire</h3>
                        <p className="mt-1 text-xs text-slate-500">Activez les jours et définissez les heures de consultation.</p>
                      </div>
                      {editingId !== null ? (
                        <div className="flex flex-col items-start gap-2 sm:items-end">
                          <span className={`text-xs font-medium ${calendarConnected ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {calendarConnected ? 'Google Calendar connecté' : 'Google Calendar non connecté'}
                          </span>
                          <button
                            type="button"
                            onClick={() => void handleConnectCalendar()}
                            disabled={connectingCalendar}
                            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                          >
                            {connectingCalendar ? 'Redirection...' : calendarConnected ? 'Reconnecter Google Calendar' : 'Connecter Google Calendar'}
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">Enregistrez le praticien avant de connecter son calendrier.</p>
                      )}
                    </div>
                    {calendarNotice && <p role="status" className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{calendarNotice}</p>}
                    <div className="space-y-2.5">
                      {weekDays.map(({ key, label }) => {
                        const day = form.availability[key];

                        return (
                          <div key={key} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-[112px_minmax(0,1fr)] sm:items-center">
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                              <input
                                type="checkbox"
                                checked={day.enabled}
                                onChange={(event) => handleAvailabilityChange(key, 'enabled', event.target.checked)}
                                className="h-4 w-4 accent-primary-600"
                              />
                              {label}
                            </label>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_112px]">
                              <input type="time" aria-label={`${label}, début`} value={day.start_time} disabled={!day.enabled} onChange={(event) => handleAvailabilityChange(key, 'start_time', event.target.value)} className="min-w-0 rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400" />
                              <input type="time" aria-label={`${label}, fin`} value={day.end_time} disabled={!day.enabled} onChange={(event) => handleAvailabilityChange(key, 'end_time', event.target.value)} className="min-w-0 rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400" />
                              <select aria-label={`${label}, durée du rendez-vous`} value={day.slot_minutes} disabled={!day.enabled} onChange={(event) => handleAvailabilityChange(key, 'slot_minutes', Number(event.target.value))} className="col-span-2 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400 sm:col-span-1">
                                {[15, 20, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{minutes} min</option>)}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Disponible</label>
                      <select value={String(form.is_available_now)} onChange={(e) => handleChange('is_available_now', e.target.value === 'true')} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none">
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Tier</label>
                      <select value={form.subscription_tier} onChange={(e) => handleChange('subscription_tier', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none">
                        <option value="premium">Premium</option>
                        <option value="ars">ARS</option>
                        <option value="af">AF</option>
                        <option value="convention">Convention</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Mise en avant</label>
                      <select value={String(form.is_featured)} onChange={(e) => handleChange('is_featured', e.target.value === 'true')} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none">
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                      </select>
                    </div>
                  </div>

                  {form.subscription_tier === 'convention' && (
                    <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-4">
                      <label className="mb-1.5 block text-sm font-semibold text-primary-900">Code partenaire</label>
                      <input
                        value={form.convention_code}
                        onChange={(e) => handleChange('convention_code', e.target.value)}
                        placeholder="Saisir le code partenaire"
                        className="w-full rounded-xl border border-primary-200 bg-white px-3 py-2.5 focus:border-primary-500 focus:outline-none"
                        required
                      />
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className={editingId === null ? 'sm:col-span-2' : ''}>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Note</label>
                      <input type="number" step="0.1" value={form.rating} onChange={(e) => handleChange('rating', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                    </div>
                    {editingId !== null && (
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">Avis</label>
                        <input type="number" value={form.reviews} onChange={(e) => handleChange('reviews', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                      </div>
                    )}
                  </div>

                  {editingId !== null && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Prochain créneau</label>
                      <input value={form.next_slot} onChange={(e) => handleChange('next_slot', e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:outline-none" />
                    </div>
                  )}
                </div>

                {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">{error}</div>}

                <button type="submit" disabled={loading} className="mt-6 w-full rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70">
                  {loading ? 'Enregistrement...' : editingId !== null ? 'Mettre à jour le praticien' : 'Enregistrer le praticien'}
                </button>
              </form>

              <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Catalogue</p>
                      <h2 className="mt-1 text-xl font-semibold text-slate-900">Liste des praticiens</h2>
                    </div>
                    <div className="flex w-full flex-col gap-2 md:max-w-md md:flex-row md:items-center md:justify-end">
                      <input
                        value={practitionerSearch}
                        onChange={(event) => {
                          setPractitionerSearch(event.target.value);
                          setPractitionerPage(1);
                        }}
                        placeholder="Rechercher..."
                        className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm md:max-w-xs focus:border-primary-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleExportPractitioners}
                        className="rounded-xl border border-slate-300 bg-slate-100 px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                      >
                        Exporter CSV
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-600">
                          <th className="py-3 pr-4">Nom</th>
                          <th className="py-3 pr-4">Spécialité</th>
                          <th className="py-3 pr-4">Ville</th>
                          <th className="py-3 pr-4">Quartier</th>
                          <th className="py-3 pr-4">Statut</th>
                          <th className="py-3 pr-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPractitioners.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-4 text-slate-500">Aucun praticien ne correspond à votre recherche.</td>
                          </tr>
                        ) : (
                          paginatedPractitioners.map((practitioner) => (
                            <tr key={String(practitioner.id)} className="border-b border-slate-100 align-top">
                              <td className="py-3 pr-4 font-medium text-slate-900">{String(practitioner.name ?? '')}</td>
                              <td className="py-3 pr-4">{String(practitioner.type ?? '')}</td>
                              <td className="py-3 pr-4">{String(practitioner.city ?? '')}</td>
                              <td className="py-3 pr-4">{String(practitioner.district ?? '')}</td>
                              <td className="py-3 pr-4">{practitioner.is_available_now ? 'Disponible' : 'Occupé'}</td>
                              <td className="py-3 pr-4">
                                <div className="flex gap-2">
                                  <button type="button" onClick={() => handleEdit(practitioner)} className="rounded-lg border border-slate-300 bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200">
                                    Modifier
                                  </button>
                                  <button type="button" onClick={() => handleDelete(Number(practitioner.id))} className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-100">
                                    Supprimer
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredPractitioners.length > practitionerPageSize && (
                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
                      <p className="text-xs text-slate-500">Page {practitionerPage} / {practitionerTotalPages}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPractitionerPage((page) => Math.max(1, page - 1))}
                          disabled={practitionerPage === 1}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Précédent
                        </button>
                        <button
                          type="button"
                          onClick={() => setPractitionerPage((page) => Math.min(practitionerTotalPages, page + 1))}
                          disabled={practitionerPage === practitionerTotalPages}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Planning</p>
                      <h2 className="mt-1 text-xl font-semibold text-slate-900">Rendez-vous</h2>
                    </div>
                    <div className="flex w-full flex-col gap-2 md:max-w-md md:flex-row md:items-center md:justify-end">
                      <select
                        value={appointmentStatusFilter}
                        onChange={(event) => {
                          setAppointmentStatusFilter(event.target.value as 'all' | 'pending' | 'confirmed' | 'cancelled');
                          setAppointmentPage(1);
                        }}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm md:max-w-xs focus:border-primary-500 focus:outline-none"
                      >
                        <option value="all">Tous</option>
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmés</option>
                        <option value="cancelled">Annulés</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleExportAppointments}
                        className="rounded-xl border border-slate-300 bg-slate-100 px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                      >
                        Exporter CSV
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-600">
                          <th className="py-3 pr-4">Patient</th>
                          <th className="py-3 pr-4">Praticien</th>
                          <th className="py-3 pr-4">Date</th>
                          <th className="py-3 pr-4">Statut</th>
                          <th className="py-3 pr-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAppointments.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-4 text-slate-500">Aucun rendez-vous pour ce filtre.</td>
                          </tr>
                        ) : (
                          paginatedAppointments.map((appointment) => (
                            <tr key={String(appointment.id)} className="border-b border-slate-100 align-top">
                              <td className="py-3 pr-4 font-medium text-slate-900">{String(appointment.patient_name ?? '')}</td>
                              <td className="py-3 pr-4">{String(appointment.practitioner_name ?? '')}</td>
                              <td className="py-3 pr-4">{String(appointment.appointment_date ?? '')} {String(appointment.appointment_time ?? '')}</td>
                              <td className="py-3 pr-4">
                                <span className={`rounded-full px-2 py-1 text-xs font-medium ${String(appointment.status) === 'confirmed' ? 'bg-green-100 text-green-700' : String(appointment.status) === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {String(appointment.status ?? 'pending')}
                                </span>
                              </td>
                              <td className="py-3 pr-4">
                                <div className="flex gap-2">
                                  <button type="button" onClick={() => setSelectedAppointment(appointment)} className="rounded-lg border border-slate-300 bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200">
                                    Détails
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleAppointmentDecision(Number(appointment.id), 'confirmed')}
                                    disabled={String(appointment.status) === 'confirmed'}
                                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Confirmer
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleAppointmentDecision(Number(appointment.id), 'cancelled')}
                                    disabled={String(appointment.status) === 'cancelled'}
                                    className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Annuler
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredAppointments.length > appointmentPageSize && (
                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
                      <p className="text-xs text-slate-500">Page {appointmentPage} / {appointmentTotalPages}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setAppointmentPage((page) => Math.max(1, page - 1))}
                          disabled={appointmentPage === 1}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Précédent
                        </button>
                        <button
                          type="button"
                          onClick={() => setAppointmentPage((page) => Math.min(appointmentTotalPages, page + 1))}
                          disabled={appointmentPage === appointmentTotalPages}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <section id="practitioner-requests-section" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Professionnels</p>
                  <h2 className="mt-1 text-xl font-semibold text-slate-900">Demandes praticiens</h2>
                  <p className="mt-1 text-sm text-slate-500">{filteredPractitionerRequests.length} demande(s), des plus récentes aux plus anciennes</p>
                </div>
                <select
                  aria-label="Filtrer les demandes praticiens"
                  value={practitionerRequestFilter}
                  onChange={(event) => setPractitionerRequestFilter(event.target.value as typeof practitionerRequestFilter)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm md:w-64"
                >
                  <option value="all">Toutes les demandes</option>
                  <option value="fiche">Création de fiche</option>
                  <option value="quote">Demande de devis</option>
                  <option value="visibility-study">Étude de visibilité</option>
                  <option value="contact">Contact</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="py-3 pr-4">Type</th>
                      <th className="py-3 pr-4">Professionnel</th>
                      <th className="py-3 pr-4">Contact</th>
                      <th className="py-3 pr-4">Fiche</th>
                      <th className="py-3 pr-4">Reçue le</th>
                      <th className="py-3 pr-4">Détails</th>
                      <th className="py-3 pr-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPractitionerRequests.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-slate-500">Aucune demande praticien pour ce filtre.</td>
                      </tr>
                    ) : filteredPractitionerRequests.map((request) => (
                      <tr key={request.id} className="border-b border-slate-100 align-top">
                        <td className="py-3 pr-4">
                          <span className="inline-flex rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-800">{request.request_label}</span>
                        </td>
                        <td className="py-3 pr-4 font-medium text-slate-900">{request.name}</td>
                        <td className="py-3 pr-4">
                          <a className="block text-primary-700 hover:underline" href={`mailto:${request.email}`}>{request.email}</a>
                          <a className="mt-1 block text-slate-600 hover:underline" href={`tel:${request.phone}`}>{request.phone}</a>
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {request.etablissement && <p>{request.etablissement}</p>}
                          {request.specialite && <p>{request.specialite}</p>}
                          {request.category && <p>Catégorie : {request.category}</p>}
                          {[request.ville, request.quartier].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 pr-4 text-slate-600">{new Date(request.created_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                        <td className="max-w-xs py-3 pr-4 text-slate-600">
                          <details>
                            <summary className="cursor-pointer font-medium text-primary-700">Voir</summary>
                            <div className="mt-2 space-y-1 text-xs leading-5">
                              {request.fonction && <p>Fonction : {request.fonction}</p>}
                              {request.specialite && <p>Spécialité : {request.specialite}</p>}
                              {request.category && <p>Catégorie : {request.category}</p>}
                              {request.quartier && <p>Quartier : {request.quartier}</p>}
                              {request.adresse && <p>Adresse : {request.adresse}</p>}
                              {request.besoins && <p>Besoins : {request.besoins}</p>}
                              <p>{request.message || 'Aucun message'}</p>
                              <p className="text-slate-400">Statut : {request.status}</p>
                            </div>
                          </details>
                        </td>
                        <td className="py-3 pr-4">
                          {request.request_type !== 'fiche' ? (
                            '—'
                          ) : request.practitioner_id ? (
                            <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                              Ajouté à l’annuaire
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => beginPractitionerApproval(request)}
                              disabled={approvingProspectId !== null}
                              className="whitespace-nowrap rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Choisir le plan
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </main>
        </div>
      </div>

      {approvalRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <form onSubmit={handleApprovePractitionerRequest} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Validation de la fiche</p>
              <h3 className="mt-1 text-2xl font-bold text-slate-900">Publier {approvalRequest.name}</h3>
              <p className="mt-1 text-sm text-slate-600">Choisissez le plan à appliquer avant de confirmer la publication.</p>
            </div>

            <div className="mb-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
              <p><span className="font-medium">Catégorie :</span> {approvalRequest.category}</p>
              <p className="mt-1"><span className="font-medium">Spécialité :</span> {approvalRequest.specialite}</p>
              <p className="mt-1"><span className="font-medium">Ville :</span> {approvalRequest.ville}</p>
              <p className="mt-1"><span className="font-medium">Quartier :</span> {approvalRequest.quartier}</p>
            </div>

            <label className="block text-sm font-medium text-slate-700">
              Plan du praticien *
              <select
                required
                value={approvalPlan}
                onChange={(event) => setApprovalPlan(event.target.value as typeof approvalPlan)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal focus:border-primary-500 focus:outline-none"
              >
                <option value="standard">Standard</option>
                <option value="ars">ARS</option>
                <option value="premium">Premium</option>
                <option value="convention">Conventionné</option>
              </select>
            </label>

            {approvalPlan === 'convention' && (
              <label className="mt-4 block text-sm font-medium text-slate-700">
                Code partenaire *
                <input
                  required
                  maxLength={50}
                  value={approvalConventionCode}
                  onChange={(event) => setApprovalConventionCode(event.target.value)}
                  placeholder="Ex. CNOPS"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal focus:border-primary-500 focus:outline-none"
                />
              </label>
            )}

            {approvalError && (
              <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                {approvalError}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setApprovalRequest(null)}
                disabled={approvingProspectId !== null}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={approvingProspectId !== null}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60"
              >
                {approvingProspectId !== null ? 'Publication…' : 'Confirmer et publier'}
              </button>
            </div>
          </form>
        </div>
      )}

      {selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Rendez-vous</p>
                <h3 className="mt-1 text-2xl font-bold">{String(selectedAppointment.patient_name ?? 'Patient')}</h3>
              </div>
              <button type="button" onClick={() => setSelectedAppointment(null)} className="rounded-full border border-slate-300 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">
                Fermer
              </button>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 p-3">
                <span className="text-slate-500">Statut</span>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${String(selectedAppointment.status) === 'confirmed' ? 'bg-green-100 text-green-700' : String(selectedAppointment.status) === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {String(selectedAppointment.status ?? 'pending')}
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Téléphone</p>
                  <p className="mt-1 font-medium">{String(selectedAppointment.patient_phone ?? '—')}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Praticien</p>
                  <p className="mt-1 font-medium">{String(selectedAppointment.practitioner_name ?? '—')}</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Date</p>
                  <p className="mt-1 font-medium">{String(selectedAppointment.appointment_date ?? '—')}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Heure</p>
                  <p className="mt-1 font-medium">{String(selectedAppointment.appointment_time ?? '—')}</p>
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Note</p>
                <p className="mt-2 whitespace-pre-wrap text-slate-700">{String(selectedAppointment.notes ?? 'Aucune note enregistrée.')}</p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  if (selectedAppointment && selectedAppointment.id) {
                    void handleAppointmentDecision(String(selectedAppointment.id), 'confirmed');
                  }
                }}
                disabled={String(selectedAppointment.status) === 'confirmed'}
                className="flex-1 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirmer
              </button>
              <button
                type="button"
                onClick={() => {
                  if (selectedAppointment && selectedAppointment.id) {
                    void handleAppointmentDecision(String(selectedAppointment.id), 'cancelled');
                  }
                }}
                disabled={String(selectedAppointment.status) === 'cancelled'}
                className="flex-1 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
